import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { OrganizationTypes, RequestHeaders } from '@/common/constants';
import { Pagination } from '@/common/types';
import { API_URL, IOrganizationExpressSetupResponseDto } from '@/dtos';
import { EmployerStatus } from '@/modules/employer/employer.constants';

export const QUERY_KEY = 'useSubsidiaries';

export interface SubsidiaryPayload {
  id: number;
  short_name: string;
  external_identifier: string;
  name: string;
  parent_id: number;
  organization_status_type: string;
  valid_from: string;
  path: string;
  logo_info: {
    large_logo_url: string;
    minimized_logo_url: string;
    favicon_url: string;
  };
}

export interface Subsidiary {
  id: string;
  shortName: string;
  externalIdentifier: string;
  name: string;
  parentId: string;
  organizationStatusType: string;
  validFrom: string;
  path: string;
  isLocked?: boolean;
  logoInfo: {
    largeLogoUrl: string;
    minimizedLogoUrl: string;
    faviconUrl: string;
  };
}

const useSubsidiariesQuery = (withAllStatuses?: boolean, organizationPath?: string) => {
  const { isLoading, isSuccess, isError, data } = useQuery(
    [QUERY_KEY, organizationPath, withAllStatuses],
    () =>
      api.get<Pagination<IOrganizationExpressSetupResponseDto>>(API_URL.ORGANIZATIONS, {
        ...(organizationPath
          ? {
              headers: {
                [RequestHeaders.observedOrganizationPath]: organizationPath,
              },
            }
          : {}),
        params: {
          type: OrganizationTypes.subsidiary,
          ...(withAllStatuses
            ? {}
            : {
                statuses: [
                  EmployerStatus.Active,
                  EmployerStatus.TerminationInProgress,
                  EmployerStatus.PendingTermination,
                ].join(','),
              }),
          size: 100,
        },
      })
  );

  const formattedData: Subsidiary[] = useMemo(
    () =>
      data?.data?.content?.map((subsidiary): Subsidiary => {
        const formattedItem: Subsidiary = {
          id: toString(subsidiary.id),
          shortName: subsidiary.short_name || '',
          externalIdentifier: subsidiary.external_identifier || '',
          name: subsidiary.name || '',
          parentId: toString(subsidiary.parent_id),
          organizationStatusType: subsidiary.organization_status_type as string,
          validFrom: subsidiary.valid_from as unknown as string,
          path: subsidiary.path || '',
          isLocked: subsidiary.is_locked,
          logoInfo: {
            largeLogoUrl: subsidiary?.logo_info?.large_logo_url || '',
            minimizedLogoUrl: subsidiary?.logo_info?.minimized_logo_url || '',
            faviconUrl: subsidiary?.logo_info?.favicon_url || '',
          },
        };
        return formattedItem;
      }) || [],
    [data]
  );

  return {
    formattedData,
    isError,
    isLoading,
    isSuccess,
  };
};

export default useSubsidiariesQuery;
